'use client';

import React from 'react';
import FAQAccordion, {
  FAQAccordionProps,
} from '@/components/shared/FAQAccordion';
import useSWR from 'swr';
import { fetchFAQs } from '@/clients/common/faqs';
import { cn } from '@/lib/utils';

const Faqs = [
  {
    id: 1,
    question: {
      de: 'Was ist LENTHO?',
      en: 'What is LENTHO?',
    },
    answer: {
      de: 'LENTHO.com ist eine Rabatt- & Cashback-Plattform, bei der Nutzer für Einkäufe unserer Partnerunternehmen Geld zurückbekommen. Nach der Anmeldung können Nutzer über LENTHO einkaufen, von Rabatten profitieren und erhalten zusätzlich einen Teil des Kaufbetrags auf ihr LENTHO-Konto zurück erstattet.',
      en: 'LENTHO.com is a discount and cashback platform where users receive money back for purchases made with our partner companies. After registering, users can shop via LENTHO, benefit from discounts, and receive part of their purchase amount back to their LENTHO account.',
    },
  },
  {
    id: 8,
    question: {
      de: 'Werde ich belohnt, wenn ich LENTHO meinen Freunden und Community weiterempfehle?',
      en: 'Will I be rewarded if I recommend LENTHO to my friends and community?',
    },
    answer: {
      de: 'Oh ja! 👉 Mit jeder Empfehlung stärkst du nicht nur deinen Geldbeutel – sondern die ganze Community. Du erhältst für jede erfolgreiche Empfehlung einmalig 5 € deinem LENTHO-Konto gutgeschrieben. Darüber hinaus verdienst du dauerhaft 15 % an dem Cashback der Bestellungen deiner Freunde mit. <a href="https://blog.lentho.com/empfehle-lentho-verdiene/" class="font-bold">Mehr erfahren >></a>',
      en: 'Oh yes! 👉 With every referral, you not only earn cash - but for the whole community. For every successful referral, you will receive a one-off €5 credit to your LENTHO account. In addition, you earn 15% of the cashback on your referrals&apos; orders. <a href="https://blog.lentho.com/empfehle-lentho-verdiene/" class="font-bold">More information >></a>',
    },
  },
  {
    id: 2,
    question: {
      de: 'Was kannst du bei LENTHO kaufen?',
      en: 'What can you buy at LENTHO?',
    },
    answer: {
      de: 'Egal ob Lebensmittel, Mode, Elektronik, Beauty, Reisen, Dienstleistungen und vieles mehr -- bei LENTHO gibt es tolle Unternehmen und trendige Produkte.',
      en: 'Whether groceries, fashion, electronics, beauty products, travel, services, and much more -- LENTHO features great companies and trendy products.',
    },
  },
  {
    id: 3,
    question: {
      de: 'Wie funktioniert LENTHO?',
      en: 'How does LENTHO work?',
    },
    answer: {
      de: '<ul class="list-disc pl-5 space-y-2"><li><strong>Anmelden:</strong> Registriere dich bei LENTHO und bekomme 10 €* geschenkt.</li><li><strong>Einkaufen:</strong> Wähle einen Online-Shop oder ein Unternehmen in deiner Stadt aus, klicke auf Onlineshop und nutze den möglichen Deal oder Rabattcode.</li><li><strong>Cashback erhalten:</strong> Nach dem Kauf bekommst du einen Teil des Kaufbetrags als Cashback, der dir auf deinem LENTHO-Konto gutgeschrieben wird, sobald das Partner-Unternehmen diesen freigegeben hat. Das Cashback variiert je nach Partner und Angebot.</li><li><strong>Auszahlung:</strong> Sobald du 20 € Cashback gesammelt hast, kannst du es auf dein PayPal Konto auszahlen lassen.</li></ul>',
      en: '<ul class="list-disc pl-5 space-y-2"><li><strong>Sign up:</strong> Register at LENTHO and receive €10* as a welcome gift.</li><li><strong>Buy:</strong> Choose an online shop or a local business in your city, click on "Online Shop," and take advantage of any available deals or discount codes.</li><li><strong>Receive cashback:</strong> After your purchase, a portion of the amount will be credited to your LENTHO account as cashback once the partner company has approved it. The cashback amount varies depending on the partner and the offer.</li><li><strong>Payout:</strong> As soon as you\'ve earned €20 in cashback, you can withdraw it to your PayPal account.</li></ul>',
    },
  },
  {
    id: 4,
    question: {
      de: 'Ist LENTHO für mich kostenlos?',
      en: 'Is LENTHO free for me?',
    },
    answer: {
      de: 'LENTHO ist für dich <strong>100 % kostenlos</strong> und du kannst durch Cashback und Weiterempfehlungen <strong>sogar noch Geld</strong> verdienen.',
      en: 'LENTHO is <strong>100% free</strong> for you, and you can even earn money through cashback and referrals.',
    },
  },
  {
    id: 5,
    question: {
      de: 'Warum habe ich kein Cashback erhalte?',
      en: "Why haven't I received any cashback?",
    },
    answer: {
      de: 'Es gibt mehrere Gründe, warum du möglicherweise kein Cashback bei LENTHO erhalten hast:<ul class="list-disc pt-4 pl-5 space-y-2"><li><strong>Einkauf über die falsche Seite</strong>: Um Cashback zu erhalten, musst du sicherstellen, dass du den Kauf über den LENTHO-Link oder die LENTHO-App tätigst. Einkäufe, die direkt auf den Partner-Websites ohne die Nutzung der LENTHO-Plattform gemacht werden, werden nicht erfasst.</li><li><strong>Cookies deaktiviert:</strong> Wenn Cookies in deinem Browser deaktiviert sind, kann LENTHO deinen Einkauf möglicherweise nicht nachverfolgen. Stelle sicher, dass Cookies aktiviert sind, wenn du über LENTHO einkaufst.</li><li><strong>Fehlerhafte Tracking-Daten:</strong> In seltenen Fällen kann es vorkommen, dass das Cashback aufgrund von Tracking-Problemen nicht korrekt erfasst wird.</li><li><strong>Rücksendungen oder Stornierungen:</strong> Wenn du den Kauf später stornierst oder die Ware zurückschickst, wird das Cashback nicht gutgeschrieben.</li><li><strong>Lange Bearbeitungszeiten:</strong> Manche Händler brauchen länger, um das Cashback zu bestätigen. Dies kann mehrere Wochen dauern, besonders bei größeren Einkäufen oder speziellen Angeboten.</li></ul>',
      en: 'There are several reasons why you may not have received cashback on LENTHO:<ul class="list-disc pl-5 space-y-2"><li><strong>Purchase through the wrong site:</strong> To receive cashback, you must make the purchase using the LENTHO link or via the LENTHO app. Purchases made directly on partner websites without using LENTHO cannot be tracked.</li><li><strong>Cookies disabled:</strong> If cookies are disabled in your browser, LENTHO may not be able to track your purchase. Make sure cookies are enabled when shopping via LENTHO.</li><li><strong>Incorrect tracking data:</strong> In rare cases, cashback might not be tracked correctly due to tracking issues.</li><li><strong>Returns or cancellations:</strong> If you cancel your purchase or return the goods, no cashback will be credited.</li><li><strong>Long processing times:</strong> Some merchants take longer to confirm cashback. This can take several weeks, especially for larger purchases or special offers.</li></ul>',
    },
  },
  {
    id: 6,
    question: {
      de: 'Kann mein Cashback verfallen?',
      en: 'Can my cashback expire?',
    },
    answer: {
      de: 'Cashback und Punkte verfallen nach 12 Monaten Inaktivität (keine Käufe, keine Nutzung der Plattform). Aber keine Sorge! Nutzer werden 30 Tage vor Verfall per E-Mail erinnert.',
      en: "Cashback and points expire after 12 months of inactivity (no purchases, no platform usage). But don't worry -- users will be reminded by email 30 days before expiration.",
    },
  },
  {
    id: 7,
    question: {
      de: 'Wie kann ich kündigen und mein Konto löschen?',
      en: 'How can I cancel and delete my account?',
    },
    answer: {
      de: 'Du kannst dein Konto jederzeit kündigen und löschen. Dabei verfallen alle ausstehenden Cashback-Beträge & Provisionen.',
      en: 'You can cancel and delete your account at any time. Please note that any outstanding cashback amounts & commissions will be forfeited upon deletion.',
    },
  },
];

const getFaq = (lang: string) => {
  return Faqs.map((item: any) => {
    return {
      ...item,
      question: item.question[lang],
      answer: item.answer[lang],
    };
  });
};

const fetcher = async (locale?: string) => {
  return fetchFAQs({
    filter: {
      location: 'homepage',
      // per_page: 100,
      order_by: 'order_index',
      order_dir: 'asc',
    },
    headers: {
      ['Accept-Language']: locale || 'en',
    },
  }).then((res) => res?.data);
};

type Props = {
  params: any;
  dictionary: any;
};

export default function FAQSection({ params, dictionary }: Props) {
  const [lang] = params.locale.split('-');

  const {
    data: resData,
    error,
    isLoading,
  } = useSWR(
    ['homepage-faqs', params?.locale],
    ([, locale]) => fetcher(locale),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (error) {
    return (
      <section id="fragen">
        <div className="container">
          <h1 className="text-left text-h1 font-semibold text-title">
            {dictionary.common.faq}
            {lang === 'de'
              ? `${dictionary.common.faq} konnten nicht geladen werden`
              : `failed to load ${dictionary.common.faq}`}
          </h1>
        </div>
      </section>
    );
  }

  return (
    <section id="fragen" className="py-12 lg:py-[90px]">
      <div className="container">
        <h1 className="text-left text-h1 font-semibold text-title">
          {dictionary.common.faq}
        </h1>
        <div className="mb-10 mt-[50px] flex items-center justify-center">
          <div className="flex w-full items-center justify-center">
            {isLoading ? (
              <FAQSkeleton count={3} />
            ) : (
              <div id="store-faqs" className="faq-list w-full space-y-3">
                {resData?.data?.map((faq: FAQAccordionProps) => {
                  return (
                    <FAQAccordion
                      key={faq.id}
                      id={faq.id}
                      question={faq.question}
                      answer={faq.answer}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 text-sm text-gray-800">
          <p
            dangerouslySetInnerHTML={{
              __html: dictionary.storefront.home.faq.disclaimerText,
            }}
          />
        </div>
      </div>
    </section>
  );
}

function FAQSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="container space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={cn(
            'animate-pulse rounded-[10px] border border-gray-200 p-7'
          )}
        >
          <div className="mb-3 h-5 w-3/4 rounded bg-gray-200" />
          <div className="h-4 w-full rounded bg-gray-100" />
          <div className="mt-2 h-4 w-5/6 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  );
}
